import type { AuthSessionStatusResponse } from '@sola/schema/dotcom/auth.ts';
import type { AccountCreatePayload } from '@sola/schema/dotcom/auth/account.ts';
import type {
  AuthPayload,
  AuthResponse,
  AuthSignupResponse,
  ConfirmCreateUserPayload,
  CreateUserPayload,
  ForgotPasswordValidatePayload,
  ForgotResponse,
  RecoverUsernamePayload,
  ResendConfirmationEmailPayload,
  ResendConfirmationResponse,
  UpdatePreferredUsernameResponse,
  VerifyResponse,
} from '@sola/schema/dotcom/auth/api.ts';
import { AuthSearchParam } from '@sola/schema/dotcom/auth/routes.ts';
import type { AnyObject } from '@sola/schema/dotcom/helpers.ts';
import {
  httpGet,
  httpGetWithStatus,
  httpPostSigned,
  httpPutSigned,
} from '@sola/shared-utils/httpClient.ts';
import type { RequestOptions } from '@sola/shared-utils/httpClient.ts';
import {
  trackCreateAccount,
  trackLogin,
} from '@sola/tracking/lib/analytics/events.ts';

// The NEXT_PUBLIC_BASE_URL_AUTH env value isn't required in the auth app
const apiPrefix =
  process.env.NEXT_PUBLIC_BASE_URL_AUTH || process.env.BASE_URL_AUTH || '';

const authEndpoints = {
  accounts: `${apiPrefix}/api/accounts`,
  create: `${apiPrefix}/api/create`,
  createValidate: `${apiPrefix}/api/create-validate`,
  logIn: `${apiPrefix}/api/login`,
  logOut: `${apiPrefix}/api/logout`,
  password: `${apiPrefix}/api/password`,
  passwordValidate: `${apiPrefix}/api/password-validate`,
  resendConfirmation: `${apiPrefix}/api/resend-confirmation`,
  session: `${apiPrefix}/api/session`,
  signupValidationCodeResend: `${apiPrefix}/api/signups/validation-code/resend`, // TODO Remove this, with not needing resend anymore
  signup: `${apiPrefix}/api/signups/start`,
  signupValidate: `${apiPrefix}/api/signups/validate`,
  username: `${apiPrefix}/api/username`,
  verify: `${apiPrefix}/api/verify`,
  usersUsername: (userId: string) =>
    `${apiPrefix}/api/users/${userId}/username`,
  usersPassword: (userId: string) =>
    `${apiPrefix}/api/users/${userId}/password`,

  users: `${apiPrefix}/api/users`,
};

const defaultApiOptions: RequestInit = {
  credentials: 'include',
};

const httpPostCredentials = async <T>(
  endpoint: RequestInfo,
  data?: AnyObject,
  options?: RequestOptions,
) => {
  return await httpPostSigned<T>(endpoint, data ?? {}, {
    ...(options && { options }),
    ...defaultApiOptions,
  });
};

/**
 * Authenticates the user, and stores initial auth user object in redis cache, and returns it.
 */
export const authenticateUser = async (
  payload: AuthPayload,
): Promise<AuthResponse> => {
  const authResponse = await httpPostCredentials<AuthResponse>(
    authEndpoints.logIn,
    payload,
  );

  if (authResponse?.success) {
    trackLogin(authResponse);
  }

  return authResponse;
};

/**
 * Checks if user is authenticated, then returns auth user from redis cache.
 */
export const verifyUser = async (): Promise<VerifyResponse> => {
  // TODO: Why is this a POST without any data? Could it not just be a get?
  return await httpPostCredentials(authEndpoints.verify);
};

/**
 * Fetches the current authentication session expiration without extending it.
 */
export const getSessionStatus = async () =>
  await httpGetWithStatus<AuthSessionStatusResponse>(authEndpoints.session, {
    credentials: 'include',
  });

/**
 * Extends the authenticated session and returns its new expiration.
 */
export const extendSession = async () =>
  await httpPostCredentials<AuthSessionStatusResponse>(authEndpoints.session);

/**
 * Logs user out in Cognito, deletes redis cache & removes cookies from header.
 */
export const logoutUser = async () => {
  return await httpPostCredentials(authEndpoints.logOut);
};

/**
 * Create the user with either the email, sending the user on the signup scenario, or globalId and postalCode
 * and stores initial auth user object in redis cache, and returns it. // TODO: Do we return AuthUserCacheAttributesWithNoTokens when created with GlobalId to log user in right away?
 */
export const userCreateFromAccount = async (
  payload: CreateUserPayload,
): Promise<AuthResponse> => {
  const authResponse = await httpPostCredentials<AuthResponse>(
    authEndpoints.create,
    payload,
  );

  if (authResponse?.success && payload.globalId && payload.postalCode) {
    trackCreateAccount();
  }

  return authResponse;
};

export const userCreate = async (payload: {
  username: string;
  password: string;
  sessionId: string;
}): Promise<AuthResponse> => {
  const authResponse = await httpPostCredentials<AuthResponse>(
    authEndpoints.users,
    payload,
  );

  if (authResponse?.success) {
    trackCreateAccount();
  }

  return authResponse;
};

export const accountCreate = async (
  payload: AccountCreatePayload,
): Promise<AuthSignupResponse> => {
  const authResponse = await httpPostCredentials<AuthSignupResponse>(
    authEndpoints.accounts,
    payload,
  );

  if (authResponse?.success) {
    // TODO: Since this is just the begining of this flow should we have a different event?
    trackCreateAccount(true);
  }

  return authResponse;
};

export const signup = async (
  email: string,
  app?: string,
  returnUrl?: string,
): Promise<AuthResponse> => {
  const authResponse = await httpPostCredentials<AuthResponse>(
    authEndpoints.signup,
    {
      email,
      app,
      returnUrl,
    },
  );

  if (authResponse?.success) {
    // TODO: Since this is just the begining of this flow should we have a different event?
    trackCreateAccount(true);
  }

  return authResponse;
};

/**
 * Verify the validation code when creating an account with an email address
 */
export const confirmAccountEmail = async (
  payload: ConfirmCreateUserPayload,
): Promise<AuthResponse> => {
  const confirmResponse = await httpPostCredentials<AuthResponse>(
    authEndpoints.createValidate,
    payload,
  );

  if (confirmResponse?.success) {
    trackCreateAccount(true);
  }

  return confirmResponse;
};

/**
 * Create the user with either the email, sending the user on the signup scenario, or globalId and postalCode
 * and stores initial auth user object in redis cache, and returns it.
 */
export const resendConfirmationEmail = async (
  payload: ResendConfirmationEmailPayload,
): Promise<ResendConfirmationResponse> => {
  return await httpPostCredentials(authEndpoints.resendConfirmation, payload);
};

interface ForgotPasswordPayload {
  email: string;
  username?: string;
  globalId?: string;
  app?: string;
  returnUrl?: string;
}

/**
 * Initiate the password reset process
 */
export const forgotPassword = async (
  payload: ForgotPasswordPayload,
): Promise<ForgotResponse> => {
  return await httpPostCredentials(authEndpoints.password, payload);
};

/**
 * Validate the confirmation code and reset the password
 */
export const forgotPasswordValidate = async (
  payload: ForgotPasswordValidatePayload,
): Promise<ForgotResponse> => {
  return await httpPostCredentials(authEndpoints.passwordValidate, payload);
};

/**
 * Create the user with either the email, sending the user on the signup scenario, or globalId and postalCode
 * and stores initial auth user object in redis cache, and returns it.
 */
export const recoverUsername = async (
  payload: RecoverUsernamePayload,
): Promise<AuthResponse> => {
  return await httpPostCredentials(authEndpoints.username, payload);
};

/**
 * Validate the signup code via GET request with sessionId and code as query params.
 */
export const signupValidate = async (
  sessionId: string,
  code: string,
): Promise<AuthSignupResponse | null> => {
  try {
    const queryParams = new URLSearchParams({
      [AuthSearchParam.SessionId]: sessionId,
      [AuthSearchParam.Code]: code,
    });
    return await httpGet<AuthSignupResponse>(authEndpoints.signupValidate, {
      queryParams,
      ...defaultApiOptions,
    });
  } catch {
    return null;
  }
};

/*
 * Updates the current user's preferred username.
 */
export const updatePreferredUsername = async (
  authUsername: string,
  preferredUsername: string,
): Promise<UpdatePreferredUsernameResponse> => {
  return await httpPutSigned(
    authEndpoints.usersUsername(authUsername),
    { preferredUsername },
    defaultApiOptions,
  );
};

/**
 * Updates the authenticated user's password.
 */
export const updatePassword = async (
  authUsername: string,
  password: string,
): Promise<AuthResponse> => {
  return await httpPutSigned(
    authEndpoints.usersPassword(authUsername),
    { password },
    defaultApiOptions,
  );
};
